import { z } from "zod";

/**
 * The About dialog's data (openspec change `release-and-ci-deploy`, design D5), inlined at build
 * time by `vite.config.ts` `define` from `scripts/changelog.ts` `releaseNotes()`: this build's
 * version and the user-facing What's new of the last five releases (a curated
 * `docs/releases/<version>.md` replaces a release's generated entries), at most five entries per
 * group with a count of the rest. Entries are plain text and links, rendered as React text:
 * never markup.
 */

// Replaced at build time (vite.config.ts `define`); undefined under tests and `bun --watch`.
export const VERSION = String(import.meta.env.ESI_VERSION ?? "dev");

const inlineSchema = z.object({ text: z.string(), href: z.string().optional() });

export const releaseNotesSchema = z.object({
  releases: z.array(
    z.object({
      version: z.string(),
      date: z.string(),
      curated: z.boolean(),
      // The release's full notes: "and N more" links here.
      notesUrl: z.string().optional(),
      groups: z.array(
        z.object({
          kind: z.enum(["features", "fixes", "notes"]),
          title: z.string().optional(),
          entries: z.array(z.array(inlineSchema)),
          // Entries left out by the cap (scripts/changelog.ts `WHATS_NEW_CAP`).
          more: z.number().int().positive().optional(),
        }),
      ),
    }),
  ),
});

export type ReleaseNotes = z.infer<typeof releaseNotesSchema>;
export type NotesRelease = ReleaseNotes["releases"][number];
export type NotesInline = z.infer<typeof inlineSchema>;

/** Bundled notes that do not parse are no notes (About says so), never a broken dialog. */
export function parseReleaseNotes(value: unknown): ReleaseNotes {
  const parsed = releaseNotesSchema.safeParse(value);
  return parsed.success ? parsed.data : { releases: [] };
}

export const RELEASE_NOTES = parseReleaseNotes(import.meta.env.ESI_RELEASE_NOTES);

/** Only web links are rendered as links; anything else shows as its text. */
export function safeHref(href: string | undefined): string | undefined {
  return href !== undefined && /^(?:https?:\/\/|mailto:)/i.test(href) ? href : undefined;
}
